import * as amplitude from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import { mapGetters, mapState } from "vuex";
import { launchdarklyKeys } from "@/utils/launchdarklyKeys";
import { getFlagRealtimeValue } from "@/utils/helper";

import config from "../../public/harness_config.json";

async function addAmplitudeSessionReplay() {
  // required for amplitude session replay
  // launch darkly key is admin-dashboard-amplitude-session-replay-enabled
  const { amplitudeSessionReplayEnabled } = launchdarklyKeys;
  if (await getFlagRealtimeValue(amplitudeSessionReplayEnabled)) {
    const sessionReplayTracking = sessionReplayPlugin({
      sampleRate: 0.1,
      privacyConfig: {
        defaultMaskLevel: "medium",
      },
    });
    amplitude.add(sessionReplayTracking);
  }
}

export function amplitudeInit() {
  if (config?.amplitude?.apiKey) {
    amplitude.init(config.amplitude.apiKey, {
      autocapture: true,
    });
  }
}

export const AmplitudeMixin = {
  async mounted() {
    this.identifyUser();
    this.identifyCharity();
    await addAmplitudeSessionReplay();
  },

  computed: {
    ...mapState({
      activeCharityInfo: (state) => state.charities.activeCharityInfo,
      adminInfo: (state) => state.auth.adminInfo,
      accessToken: (state) => state.auth.accessToken,
    }),

    ...mapGetters("auth", [
      "isAuthenticated",
      "verifiedTokenClaims",
      "isGodAdmin",
    ]),
  },

  methods: {
    identifyUser() {
      if (!this.isAuthenticated || !this.adminInfo) {
        return;
      }

      amplitude.setUserId(this.verifiedTokenClaims.client_id);

      if (config?.amplitude?.excludeAdmins) {
        amplitude.setOptOut(this.isGodAdmin);
      }

      const identifyEvent = new amplitude.Identify();
      identifyEvent.set("id", this.adminInfo.id);
      identifyEvent.set("email", this.adminInfo.email);
      identifyEvent.set("firstName", this.adminInfo.first_name);
      identifyEvent.set("lastName", this.adminInfo.last_name);
      amplitude.identify(identifyEvent);
    },
    identifyCharity() {
      if (!this.isAuthenticated || !this.activeCharityInfo) {
        return;
      }

      // @TODO If we add the Groups feature, we can set group info here.
    },
  },

  watch: {
    isAuthenticated(isLoggedIn, wasLoggedIn) {
      // If the user has just logged out
      if (!isLoggedIn && wasLoggedIn) {
        amplitude.reset();
      }
    },
    adminInfo: {
      handler: function (adminInfo) {
        if (!this.isAuthenticated || !adminInfo) {
          return;
        }

        this.identifyUser();
      },
      deep: true,
    },
    activeCharityInfo: {
      handler: function (charity) {
        if (!this.isAuthenticated || !charity) {
          return;
        }

        this.identifyCharity();
      },
      deep: true,
    },
  },
};
